import { inject, Injectable } from '@angular/core';
import { SqliteService } from '@app/services';
import { ReturnItemModel, RouteModel, StoreModel } from '@app/models';
import { UserRepository } from './user.repository';
import { ProductRepository } from './product.repository';

@Injectable({
    providedIn: 'root',
})
export class ReturnRepository
{
    private readonly keys = {
        tableName: 'return_items',
        productsTable: 'products',
    };
    private sqliteService = inject(SqliteService);
    private userRepository = inject(UserRepository);
    private productRepository = inject(ProductRepository);

    async clearUserRouteReturnItems(routeId: number): Promise<void>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        const sql = `
            DELETE
            FROM ${this.keys.tableName}
            WHERE route_id = ?
              AND user_id = ?;
        `;
        await this.sqliteService.delete(sql, [routeId, user.id]);
    }

    async clearReturnItems(checkInId: number, storeId: number, routeId: number): Promise<void>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        const sql = `
            DELETE
            FROM ${this.keys.tableName}
            WHERE check_in_id = ?
              AND store_id = ?
              AND route_id = ?
              AND user_id = ?;
        `;
        await this.sqliteService.delete(sql, [checkInId, storeId, routeId, user.id]);
    }

    async loadReturnItems(checkInId: number, store: StoreModel, route: RouteModel): Promise<ReturnItemModel[]>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return [];
        }

        const sql = `
            SELECT *
            FROM ${this.keys.tableName}
            WHERE check_in_id = ?
              AND store_id = ?
              AND route_id = ?
              AND user_id = ?;
        `;
        const data = await this.sqliteService.get(sql, [checkInId, store.id, route.id, user.id]) ?? [];
        const returnItems: ReturnItemModel[] = [];

        for (const item of data)
        {
            const product = await this.productRepository.getProductById(
                item.product_id,
                { routeExternalId: route.external_id, storeExternalId: store.external_id, },
            );

            if (product)
            {
                returnItems.push({
                    quantity: item.quantity,
                    subtotal: item.quantity * (item.price ? Number(item.price) : 0),
                    product: product,
                    return_reason_id: item.return_reason_id,
                });
            }
        }

        return returnItems.sort((a, b) =>
        {
            const aProduct = a.product;
            const bProduct = b.product;
            // Handle null sort_order cases
            if (aProduct.sort_order === null && bProduct.sort_order === null)
            {
                return aProduct.name.localeCompare(bProduct.name);
            }

            if (aProduct.sort_order === null)
            {
                return 1;
            }

            if (bProduct.sort_order === null)
            {
                return -1;
            }

            // Both have sort_order values
            if (aProduct.sort_order !== bProduct.sort_order)
            {
                return aProduct.sort_order - bProduct.sort_order;
            }

            // Same sort_order, sort by name
            return aProduct.name.localeCompare(bProduct.name);
        });
    }

    async isReturnEmpty(checkInId: number, storeId: number, routeId: number): Promise<boolean>
    {
        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return true;
        }

        const sql = `
            SELECT *
            FROM ${this.keys.tableName}
            WHERE check_in_id = ?
              AND store_id = ?
              AND route_id = ?
              AND user_id = ?;
        `;
        const data = await this.sqliteService.get(sql, [checkInId, storeId, routeId, user.id]) ?? [];

        return data.length <= 0;
    }

    async saveReturnItems(items: ReturnItemModel[], checkInId: number, storeId: number, routeId: number): Promise<void>
    {
        if (items.length <= 0)
        {
            return;
        }

        const user = await this.userRepository.getLoggedInUser();

        if (!user)
        {
            return;
        }

        const columns = [
            'user_id',
            'product_id',
            'check_in_id',
            'route_id',
            'store_id',
            'quantity',
            'return_reason_id',
            'price',
        ];
        const valuePlaceholders = this.sqliteService.getValuePlaceholder(items.length, columns.length);
        const sql = `
            INSERT INTO ${this.keys.tableName} (${columns.join(', ')})
            VALUES ${valuePlaceholders};
        `;
        const values: any[] = items.flatMap((p) => [
            user.id,
            p.product.id,
            checkInId,
            routeId,
            storeId,
            p.quantity,
            p.return_reason_id,
            p.product.price,
        ]);
        await this.sqliteService.save(sql, values);
    }
}
